import { defineStore } from 'pinia';
import { api } from '../boot/axios';
import { useCaseStore } from './case';
import { useMessageStore } from './message';
import { v4 as uuidv4 } from 'uuid';

export const useActivationStore = defineStore('activation', {
  persist: false,

  state: () => ({
    attachments: {},
  }),

  getters: {
    getAttachmentById: (state) => (attachmentID) => {
      return state.attachments['' + attachmentID];
    },
  },

  actions: {
    async closeActivation(activationID) {
      if (!activationID) {
        throw new Error('No activationID provided');
      }
      await api.put(`api/v2/activations/${activationID}/close`);
    },
    async postActivation(activation) {
      const caseStore = useCaseStore();
      if (activation.activationID) {
        const res = await api.put('api/v1/activations/' + activation.activationID, activation);
        await caseStore.fetchCase(activation.caseNumber);
        return res;
      } else {
        try {
          const res = await api.post('api/v1/activations', activation);
          if (res.data.masterCaseNumber) {
            await caseStore.fetchCase(res.data.masterCaseNumber);
          } else {
            await caseStore.fetchCase(res.data.patientRecordID);
          }
          return res;
        } catch (err) {
          // Only a request with no response means we are offline. If the server
          // answered with an error, the user is online, so let the caller show it.
          if (!err?.request || err.response) {
            throw err;
          }
          this.addOfflineCase(activation);
          console.error(err);
          return null;
        }
      }
    },
    addOfflineCase(offlineCase) {
      offlineCase.isTemporary = true;
      offlineCase.isReal = true;
      offlineCase.isTest = false;
      offlineCase.isNonServiced = false;
      offlineCase.patientRecordCreationDate = new Date().getTime();
      if (!offlineCase.iterations) {
        offlineCase.iterations = [];
      }
      if (!offlineCase.activations) {
        offlineCase.activations = [{ activationID: uuidv4() }];
      }
      const caseStore = useCaseStore();
      offlineCase.patientRecordID = caseStore.pendingCasesCount + 1;
      console.warn('Created offline case');
      caseStore.saveCaseLocally(offlineCase);
      return offlineCase;
    },
    async sendPendingCases() {
      const caseStore = useCaseStore();
      const messageStore = useMessageStore();
      const p = caseStore.getPendingCases;
      for (let i = 0; i < p.length; i++) {
        const oldCase = p[i];
        const iterations = JSON.parse(JSON.stringify(oldCase.iterations));
        // A pending case will have a single activation. This pending activation may have messages inside of it
        let messages = messageStore.getMessagesByActivationId(oldCase.activations[0].activationID);
        const response = await this.postActivation(oldCase);
        if (response != null) {
          if (iterations) {
            const newCase = response.data;
            for (const iteration of iterations) {
              iteration.patientRecordID = newCase.patientRecordID;
              iteration.caseNumber = newCase.patientRecordID;
              if (iteration.prescriptions) {
                for (const prescription of iteration.prescriptions) {
                  prescription.caseNumber = newCase.patientRecordID;
                }
              }
              await caseStore.postIterationNotes(iteration);
            }
          }
          if (messages) {
            messages = messages.map((message) => {
              // Do this first in case the POST fails
              message.activationID = response.data.activationID;
              return message;
            });
            for (const message of messages) {
              await messageStore.postMessage(message);
            }
          }
        }
        if (caseStore.getCaseById(oldCase.patientRecordID)) {
          caseStore.deleteCaseLocally(oldCase.patientRecordID);
        }
      }
      // return new Promise((resolve, reject) => {
      //   for (var i = 0; i < p.length; i++) {
      //     const newCase = this.postActivation(p[i]); // dispatch(POST_ACTIVATION_REQUEST, p[i])
      //     newCase.then(caseStore.deleteCaseLocally(p[i].patientRecordID));
      //   }
      //   resolve(true);
      // });
    },
    postActivationNote(activationNote) {
      return new Promise((resolve, reject) => {
        api
          .put(
            'api/v1/activations/' + activationNote.activationID + '/activationnotes',
            activationNote.note,
          )
          .then((response) => {
            resolve(response);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      });
    },
    sync() {
      // applicationStore.goOnline() calls this without awaiting, so a failure
      // has to be handled here or it surfaces as an unhandled rejection with
      // no clue which pending case caused it.
      this.sendPendingCases().catch((err) => {
        console.error('Failed to send pending cases:', err);
      });
    },
  },
});
